import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Info, X, ChevronDown, Search, Edit2 } from 'lucide-react';
import { useHistory, useLocation } from 'react-router-dom';
import Tooltip from '../addentities/Tooltip.js';
import DiscardConfirmationModal from '../../components/DiscardConfirmationModal.js';
import AlertNotification from '../../components/AlertNotification.js';
import TradeInStepper from '../../components/TradeInStepper.js';
import noImage from '../../assets/img/brand/noImage.png';
import apiHelper from '../../apiHelper.js';
import TradeInImagePickerModal from './TradeInImagePickerModal.js';
import {
    getDeviceDefaultImage,
    isDeviceDefaultUrl,
    filenameFromUrl,
    withCurrentDeviceDefault,
    dedupePoolEntries,
    maxUploadIndex,
    tabForSource,
    isRemovedImage,
    SOURCE_LISTING,
    SOURCE_ITEM,
} from './tradeInImages.js';

// ─── Constants ────────────────────────────────────────────────────────────────
const YELLOW = '#FFEB00';

// ─── Custom Radio Button ──────────────────────────────────────────────────────
function CustomRadio({ checked, onChange, label, name, value }) {
    return (
        <label
            style={{ display: 'flex', alignItems: 'center', color: '#212529', gap: 5, cursor: 'pointer', fontSize: 14, fontWeight: 400, fontFamily: 'Montserrat', userSelect: 'none' }}
            onClick={onChange}
        >
            {/* Hidden native radio for accessibility */}
            <input
                type="radio"
                name={name}
                value={value}
                checked={checked}
                onChange={onChange}
                style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }}
                readOnly
            />

            {/* Custom SVG radio */}
            {checked ? (
                /*
                 * Selected — 20×20 viewBox so the yellow circle fills exactly 20px.
                 * r=9.25 + shadow filter region expanded so the dy:1 / blur:2 shadow
                 * isn't clipped. Black dot kept at the same ~40 % ratio as the original.
                 */
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <defs>
                        <filter id="ti_radio_sel_shadow" x="-25%" y="-25%" width="150%" height="150%" colorInterpolationFilters="sRGB">
                            <feFlood floodOpacity="0" result="BackgroundImageFix" />
                            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                            <feOffset dy="1" />
                            <feGaussianBlur stdDeviation="2" />
                            <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0" />
                            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
                            <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape" />
                        </filter>
                    </defs>
                    {/* Yellow circle — r=9.25 so outer edge sits exactly at 10 (20 px) */}
                    <circle cx="10" cy="9.5" r="9.25" fill={YELLOW} filter="url(#ti_radio_sel_shadow)" />
                    {/* Black centre dot — ~38 % of outer radius to match original proportions */}
                    <circle cx="10" cy="9.5" r="3.5" fill="black" />
                </svg>
            ) : (
                /*
                 * Unselected — r=9.25 + strokeWidth=1.5 → outer stroke edge = 9.25+0.75 = 10
                 * so the circle fills exactly the 20×20 viewBox (diameter = 20 px).
                 */
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <circle cx="10" cy="10" r="9.25" stroke="#212529" strokeWidth="1.5" fill="white" />
                </svg>
            )}

            {label}
        </label>
    );
}



const SPU_LIMIT = 50;
const SPU_ENDPOINT = 'inventory/spu-sku/tradein-listing/';

function mapSpuItem(item) {
    return {
        id: item.spu_id,
        name: item.model_name || '',
        brand: item.brand_name || '',
        model: item.model_name || '',
        productType: item.product_type || '',
        brandHierarchy: item.brand_hierarchy || [],
        categoryHierarchy: item.category_hierarchy || [],
        image: item.images?.[0]?.spu_image_url || noImage,
        images: (item.images || []).map(img => ({
            id: img.spu_image_id,
            url: img.spu_image_url || noImage,
        })),
    };
}

// ─── SPU Search Dropdown (lazy-load + API search, same UI as MultiSelectSearch) ─
function SpuSearchDropdown({ selected, onSelect, onClear, error, disabled }) {
    const [open, setOpen]           = useState(false);
    const [query, setQuery]         = useState('');
    const [items, setItems]         = useState([]);
    const [loading, setLoading]     = useState(false);
    const [hasMore, setHasMore]     = useState(true);
    const [hoveredId, setHoveredId] = useState(null);
    const skipRef                   = useRef(0);
    const abortRef                  = useRef(null);
    const containerRef              = useRef(null);
    const inputRef                  = useRef(null);
    const queryRef                  = useRef(query);
    useEffect(() => { queryRef.current = query; }, [query]);

    // Close on outside click
    useEffect(() => {
        const h = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, []);

    // Clear query when closed without a selection
    useEffect(() => {
        if (!open && !selected && query.trim() !== '') setQuery('');
    }, [open, selected, query]);

    // Focus inline input when open
    useEffect(() => {
        if (open && inputRef.current) inputRef.current.focus();
    }, [open]);

    const fetchPage = useCallback(async (searchQuery, currentSkip, reset) => {
        if (abortRef.current) abortRef.current.abort();
        abortRef.current = new AbortController();
        setLoading(true);
        try {
            const params = new URLSearchParams({ skip: currentSkip, limit: SPU_LIMIT, ordering: 'model_name' });
            if (searchQuery) params.set('search', searchQuery);
            const res = await apiHelper(`${SPU_ENDPOINT}?${params}`, 'GET');
            if (!res?.ok) return;
            const data = await res.json();
            const results = (data.results || []).map(mapSpuItem);
            setItems(prev => reset ? results : [...prev, ...results]);
            setHasMore(results.length === SPU_LIMIT);
            skipRef.current = currentSkip + results.length;
        } catch (err) {
            if (err.name !== 'AbortError') console.error('SPU fetch error:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    // Load first page on open
    useEffect(() => {
        if (!open) return;
        setItems([]);
        skipRef.current = 0;
        setHasMore(true);
        fetchPage(query, 0, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Debounced API search on query change
    useEffect(() => {
        if (!open) return;
        const timer = setTimeout(() => {
            setItems([]);
            skipRef.current = 0;
            setHasMore(true);
            fetchPage(query, 0, true);
        }, 300);
        return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query]);

    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - scrollTop - clientHeight < 80 && !loading && hasMore) {
            fetchPage(queryRef.current, skipRef.current, false);
        }
    };

    return (
        <div className="multi-select" ref={containerRef} style={{ position: 'relative' }}>
            {/* Trigger box — mirrors MultiSelectSearch .select-box */}
            <div
                className="select-box form-control"
                onClick={() => { if (!disabled) setOpen(true); }}
                style={{
                    display: 'flex', flexWrap: 'wrap', alignItems: 'center',
                    cursor: 'text',
                    border: `1px solid ${error ? '#dc3545' : '#6C757D'}`,
                    height: 40,
                    paddingRight: 4,
                }}
            >
                {/* Selected item preview */}
                {selected && selected.id && (
                    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <img
                                src={selected.image || noImage}
                                alt=""
                                onError={e => { e.target.src = noImage; }}
                                style={{ width: 20, height: 20, borderRadius: 4, objectFit: 'contain', marginRight: 5 }}
                            />
                            <span style={{ fontFamily: 'Montserrat', color: '#181818', fontSize: 14, fontWeight: 400, lineHeight: '18px', marginTop: -4 }}>
                                {selected.name}
                            </span>
                        </div>
                    </div>
                )}

                {/* Inline search input */}
                <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={e => { if (!selected?.id) setQuery(e.target.value); if (!open) setOpen(true); }}
                    placeholder={!selected?.id ? 'Search and Select' : ''}
                    disabled={disabled || !!selected?.id}
                    style={{
                        border: 'none', flex: 1, outline: 'none', minHeight: 24,
                        background: 'transparent',
                        visibility: selected?.id ? 'hidden' : 'visible',
                        alignSelf: 'center', lineHeight: 'normal',
                    }}
                />

                {/* Icons */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {selected?.id && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onClear(); setQuery(''); }}
                            style={{ border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 2, marginTop: -4 }}
                            title="Clear"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                <path d="M9 3L3 9M3 3L9 9" stroke="#212529" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                    )}
                    <div
                        style={{ borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: '0.2s', cursor: 'pointer', marginRight: 7, marginTop: -4 }}
                        onClick={(e) => { e.stopPropagation(); if (!disabled) setOpen(p => !p); }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none" style={{ transform: open ? 'rotate(0deg)' : 'rotate(180deg)', transition: '0.2s' }}>
                            <path d="M4.5 11.25L9 6.75L13.5 11.25" stroke="#212529" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>
                </div>
            </div>

            {/* Dropdown list */}
            {open && !disabled && (
                <div
                    className="dropdown"
                    onScroll={handleScroll}
                    style={{ position: 'absolute', top: '100%', left: 0, width: '100%', background: '#fff', border: '1px solid #ddd', borderRadius: 6, maxHeight: 250, overflowY: 'auto', zIndex: 1000 }}
                >
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                        {items.map(item => (
                            <li
                                key={item.id}
                                onClick={() => { onSelect(item); setOpen(false); setQuery(''); }}
                                onMouseEnter={() => setHoveredId(item.id)}
                                onMouseLeave={() => setHoveredId(null)}
                                style={{ padding: '10px', cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: 2, backgroundColor: hoveredId === item.id ? '#FFFEF0' : 'transparent', borderRadius: 4 }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
                                    <img
                                        src={item.image || noImage}
                                        alt={item.name}
                                        onError={e => { e.target.src = noImage; }}
                                        style={{ width: 32, height: 32, borderRadius: 4, objectFit: 'contain', background: '#f5f5f5', flexShrink: 0 }}
                                    />
                                    <div style={{ display: 'flex', flexDirection: 'column',  minWidth: 0 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <span style={{ fontFamily: 'Montserrat', fontSize: 14, fontWeight: 400, lineHeight: '18px', color: '#212529' }}>
                                                {item.name}
                                            </span>
                                            {item.productType && (
                                                <span style={{ fontFamily: 'Montserrat', fontSize: 8, fontWeight: 600, color: '#212529', background: '#FFEB00', borderRadius: 999, padding: '2px 5px', whiteSpace: 'nowrap' }}>
                                                    {item.productType}
                                                </span>
                                            )}
                                        </div>
                                        {item.brandHierarchy.length > 0 && (
                                            <span style={{ fontFamily: 'Montserrat', fontSize: 10, lineHeight: '16px' }}>
                                                <span style={{ fontWeight: 500, color: '#6C757D' }}>Brand: </span>
                                                <span style={{ color: '#6C757D' }}>{item.brandHierarchy.map(b => b.name).join(' >> ')}</span>
                                            </span>
                                        )}
                                        {item.categoryHierarchy.length > 0 && (
                                            <span style={{ fontFamily: 'Montserrat', fontSize: 10, lineHeight: '16px' }}>
                                                <span style={{ fontWeight: 500, color: '#6C757D' }}>Category: </span>
                                                <span style={{ color: '#6C757D' }}>{item.categoryHierarchy.map(c => c.name).join(' >> ')}</span>
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </li>
                        ))}
                        {items.length === 0 && !loading && (
                            <li style={{ textAlign: 'center', padding: 8, color: '#999' }}>No results found</li>
                        )}
                        {loading && (
                            <li style={{ textAlign: 'center', padding: 8, color: '#999', fontSize: 12, fontFamily: 'Montserrat' }}>Loading...</li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
}


// ─── Main Component ───────────────────────────────────────────────────────────
export default function AddEditTradeInListing() {
    const history = useHistory();
    const location = useLocation();

    // Detect edit mode from router state
    // isEdit flag is set explicitly when navigating from Step 2 back or from the listing table edit action
    const existingListing = location.state?.listing || null;
    const isEditMode = Boolean(location.state?.isEdit) || Boolean(existingListing?.tradein_listing_id);
    const restoredListingItems = location.state?.listingItems || null;
    const restoredPricingMap = location.state?.pricingMap || null;
    // Listing-wide upload pool and carried removals, forwarded back from Step 2 (ERP-5022).
    const restoredPool = location.state?.imagePool || null;
    const carriedRemovals = location.state?.removedPoolImages || null;

    const breadcrumbs =['Listing', 'Trade-In Listing', isEditMode ? 'Edit Listing' : 'Add Listing'];

    // ── Form state ──
    // existingListing may come from:
    //   (a) API response (backend keys: listing_device, listing_brand, listing_priority, listing_id)
    //   (b) Step 2 "Back" navigation (frontend keys: listingDevice, listingBrand, listingPriority, id)
    const [formData, setFormData] = useState({
        status: existingListing?.status || existingListing?.listing_status || 'Active',
        id: existingListing?.id || existingListing?.listing_id || existingListing?.listing_code || '',
        tradein_listing_id: existingListing?.tradein_listing_id || null,
        spu: existingListing
            ? (existingListing.spu && typeof existingListing.spu === 'object')
                ? existingListing.spu
                : { id: existingListing.spu_id || null, name: existingListing.spu, brand: existingListing.listing_brand, model: existingListing.listing_model }
            : null,
        listingDevice: existingListing?.listingDevice || existingListing?.listing_device || '',
        listingBrand: existingListing?.listingBrand || existingListing?.listing_brand || '',
        listingModel: existingListing?.listingModel || existingListing?.listing_model || '',
        listingPriority: existingListing?.listingPriority != null
            ? String(existingListing.listingPriority)
            : existingListing?.listing_priority != null
                ? String(existingListing.listing_priority)
                : '',
        notes: existingListing?.notes || '',
    });

    // ── Image state ──
    // existingListing.listingImage comes from Step 2 "Back" navigation (already resolved object)
    // existingListing.tradein_image_url / user_image_name come from the API response
    //
    // A device-default snapshot (taken while an earlier Listing Device was selected) isn't a
    // real chosen image — treat it exactly like "no image saved" so it falls through to the
    // CURRENT device's placeholder instead of sticking around as a stale, mismatched image.
    // Same rule Step 2 applies to its rows.
    const [confirmedImage, setConfirmedImage] = useState(() => {
        if (existingListing?.listingImage) return existingListing.listingImage;
        const savedUrl = existingListing?.tradein_image_url || existingListing?.user_image_name || null;
        if (savedUrl && !isDeviceDefaultUrl(savedUrl)) return { id: null, url: savedUrl };
        return null;   // resolved to the device default once a device/SPU is known
    });

    const [imageModalOpen, setImageModalOpen] = useState(false);

    // ── Shared upload pool (ERP-5022) ──
    // Step 1's Listing Image and Step 2's Listing Items draw from one listing-wide pool, which
    // travels through the wizard in router state. Both steps mint `upload-N` ids into it, so
    // the counter is seeded past everything already there — otherwise this step hands out an id
    // Step 2 already used and the two different files are treated as one image.
    const [sessionUploads, setSessionUploads] = useState(() => restoredPool || []);
    const uploadCounterRef = useRef(maxUploadIndex(restoredPool));
    const addSessionUpload = (entry) =>
        setSessionUploads(prev => (
            prev.some(e => e.id === entry.id || (entry.name && e.name === entry.name))
                ? prev
                : [...prev, entry]
        ));
    const removeSessionUpload = (id) =>
        setSessionUploads(prev => prev.filter(e => e.id !== id));

    // The listing's OWN upload history (tradein_listing_images) — what this step can persist
    // and delete through its own payload, as opposed to pool entries an item owns.
    const [listingUploads, setListingUploads] = useState(() =>
        (existingListing?.listingUploads || []).map(u => ({ ...u, source: SOURCE_LISTING }))
    );
    // Ids from the list above the user removed while editing. Deletions have to be named
    // explicitly — the payload otherwise only carries brand-new files, so the backend can't
    // tell a removal from a no-op and the row survives every save.
    const [removedListingImageIds, setRemovedListingImageIds] = useState(() =>
        existingListing?.removedListingImageIds || []
    );
    // Removals aimed at ITEM rows, which only Step 2 can apply — either carried in from there
    // or produced here when the user deletes an item's tile from this Upload tab. Forwarded on.
    const [removedPoolImages, setRemovedPoolImages] = useState(() => carriedRemovals || []);

    // ── Device dropdown ──
    const [deviceOpen, setDeviceOpen] = useState(false);
    const deviceRef = useRef(null);

    // ── Validation ──
    const [errors, setErrors] = useState({});

    // ── Alert / Toast ──
    const [notification, setNotification] = useState({ show: false, type: '', action: '', recordName: '', message: '' });
    const showNotification = (type, message) =>
        setNotification({ show: true, type, action: 'create', recordName: 'Listing', message });

    // ── Device options ──
    const deviceOptions = ['Phone', 'Tablet', 'Watch', 'Accessories'];

    // In edit mode: fetch the selected SPU's images via the new paginated endpoint
    useEffect(() => {
        if (!isEditMode || !formData.spu?.id) return;
        async function fetchEditSpu() {
            try {
                const params = new URLSearchParams({ skip: 0, limit: 1, spu_id: formData.spu.id });
                const res = await apiHelper(`${SPU_ENDPOINT}?${params}`, 'GET');
                if (!res?.ok) return;
                const data = await res.json();
                const matched = (data.results || []).map(mapSpuItem).find(s => s.id === formData.spu.id);
                if (!matched) return;
                setFormData(prev => ({ ...prev, spu: matched }));
                setConfirmedImage(prev => {
                    // A placeholder follows the Listing Device, not the SPU (ERP-5022) — leave it
                    // alone, and let the device-default effect below seed a missing one. Handing
                    // it the SPU's first image here is what used to make an untouched create
                    // save an SPU image the user never chose.
                    if (prev?.isDeviceDefault || isDeviceDefaultUrl(prev?.url)) return prev;
                    if (!prev || prev.url === noImage) return null;
                    const found = matched.images?.find(img => img.url === prev.url);
                    return found || prev;
                });
            } catch (err) {
                console.error('Failed to fetch SPU for edit mode:', err);
            }
        }
        fetchEditSpu();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Keep a placeholder pointed at the CURRENT Listing Device, and seed one when nothing has
    // been chosen yet (ERP-5022). A real chosen image is left untouched — only the placeholder
    // follows the device. Gated on the SPU because the image field itself only appears once one
    // is picked; the two fields are independent, so an SPU chosen before a device resolves to
    // the Phone fallback in the meantime and re-points as soon as a device is set.
    useEffect(() => {
        if (!formData.spu) return;
        setConfirmedImage(prev => (prev
            ? withCurrentDeviceDefault(prev, formData.listingDevice)
            : { id: null, url: getDeviceDefaultImage(formData.listingDevice), isPlaceholder: true, isDeviceDefault: true }
        ));
    }, [formData.listingDevice, formData.spu]);

    // Seed the shared Upload tab on a fresh edit (ERP-5022). Entering from the listing table
    // there's no forwarded pool, so without this the picker opens on just the device default
    // even though the listing and its items already have upload history. The listing's own rows
    // ride in on the listing payload (tradein_images); the items' need a fetch.
    useEffect(() => {
        if (restoredPool) return;
        const own = (existingListing?.tradein_images || [])
            .filter(img => !isDeviceDefaultUrl(img.url))
            .map(img => ({
                id: img.id,
                file: null,
                preview: img.url,
                // A row saved before filenames were stored falls back to the URL's last segment,
                // so it still gets a name to show on hover instead of a blank tooltip.
                name: img.filename || filenameFromUrl(img.url),
                isSaved: true,
                source: SOURCE_LISTING,
            }));
        setListingUploads(own);
        setSessionUploads(dedupePoolEntries(own));
        uploadCounterRef.current = maxUploadIndex(own);

        // Which tile is the active image. The backend re-snapshots it under its own filename on
        // every save, so its URL never matches any history row — but it does echo the original
        // filename back as user_image_name, and that DOES match one of tradein_images[].filename.
        // Without this the picker opens on a non-deletable "current image" pin instead of the
        // real tile. Same trick Step 2 already used.
        const activeName = existingListing?.user_image_name || null;
        const ownMatch = activeName ? own.find(u => u.name === activeName) : null;
        if (ownMatch) {
            setConfirmedImage({
                id: ownMatch.id, url: ownMatch.preview,
                source: SOURCE_LISTING, tab: tabForSource(SOURCE_LISTING),
            });
        }

        const listingId = existingListing?.tradein_listing_id;
        if (!isEditMode || !listingId) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const res = await apiHelper(
                    `sales/trade-in-listings/${listingId}/items/?include_draft=true&page=1&page_size=200`,
                    'GET',
                );
                if (!res?.ok) return;
                const data = await res.json();
                const savedItems = Array.isArray(data) ? data : (data?.results || []);
                const itemTiles = [];
                savedItems.forEach(si => (si.tradein_images || []).forEach(img => {
                    if (isDeviceDefaultUrl(img.url)) return;
                    itemTiles.push({
                        id: img.id,
                        file: null,
                        preview: img.url,
                        name: img.filename || filenameFromUrl(img.url),
                        isSaved: true,
                        source: SOURCE_ITEM,
                    });
                }));
                if (cancelled || !itemTiles.length) return;
                setSessionUploads(prev => dedupePoolEntries([...prev, ...itemTiles]));
                // The listing can be using a file an ITEM uploaded — no row of its own matches
                // then, and the pooled tile is what the picker has to open on.
                if (!ownMatch && activeName) {
                    const pooledMatch = itemTiles.find(u => u.name === activeName);
                    if (pooledMatch) {
                        setConfirmedImage(prev => (
                            prev?.isDeviceDefault ? prev : {
                                id: pooledMatch.id, url: pooledMatch.preview,
                                source: SOURCE_ITEM, tab: tabForSource(SOURCE_ITEM),
                            }
                        ));
                    }
                }
            } catch (err) {
                console.error('Failed to prefetch listing image pool:', err);
            }
        })();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Removals that arrived from Step 2 aimed at LISTING rows — the user deleted one of this
    // listing's uploads from an item's shared Upload tab, and only this step's payload can
    // actually delete it. Item-owned entries stay put and ride forward again on Next.
    useEffect(() => {
        const carried = removedPoolImages.filter(r => r.source === SOURCE_LISTING);
        if (!carried.length) return;
        const mine = carried.map(r => r.id);
        setRemovedListingImageIds(prev => [...prev, ...mine.filter(id => !prev.includes(id))]);
        setListingUploads(prev => prev.filter(u => !isRemovedImage(
            { id: u.id, url: u.preview, name: u.name }, carried,
        )));
        setSessionUploads(prev => prev.filter(e => !isRemovedImage(
            { id: e.id, url: e.preview, name: e.name }, carried,
        )));
        setConfirmedImage(prev => (isRemovedImage(prev, carried)
            ? { id: null, url: getDeviceDefaultImage(formData.listingDevice), isPlaceholder: true, isDeviceDefault: true }
            : prev));
        setRemovedPoolImages(prev => prev.filter(r => r.source !== SOURCE_LISTING));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── ID uniqueness check ──
    const [idCheckLoading, setIdCheckLoading] = useState(false);

    const checkIdExists = async (idValue) => {
        const trimmed = (idValue || '').trim();
        if (!trimmed) return false;
        // In edit mode, if the ID hasn't changed from the original, it's always valid
        if (isEditMode) {
            const originalId = (
                existingListing?.id ||
                existingListing?.listing_id ||
                existingListing?.listing_code ||
                ''
            ).trim();
            if (trimmed === originalId) return false;
        }
        try {
            const res = await apiHelper(`sales/trade-in-listings/?listing_code=${encodeURIComponent(trimmed)}`, 'GET');
            if (!res?.ok) return false;
            const data = await res.json();
            const results = Array.isArray(data) ? data : (data.results || []);
            // Always filter by exact listing_code match — the API may return all results
            // if it doesn't support listing_code as a filter param
            const conflicts = results.filter(r => {
                if (r.listing_code !== trimmed) return false;
                if (isEditMode) return String(r.tradein_listing_id) !== String(formData.tradein_listing_id);
                return true;
            });
            return conflicts.length > 0;
        } catch {
            return false;
        }
    };

    // ── Dirty / discard ──
    const [isDirty, setIsDirty] = useState(location.state?.isDirty || false);
    const [discardModalOpen, setDiscardModalOpen] = useState(false);
    const [isDiscarding, setIsDiscarding] = useState(false);
    const [nextLocation, setNextLocation] = useState(null);
    const unblockRef = useRef(null); // holds the history.block unblock fn so handleNext can bypass it

    // ── Close device dropdown on outside click ──
    useEffect(() => {
        const handler = (e) => {
            if (deviceRef.current && !deviceRef.current.contains(e.target)) setDeviceOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // ── Navigation block when dirty ──
    useEffect(() => {
        if (!isDirty || isDiscarding) return;
        const unblock = history.block((tx) => {
            setNextLocation(tx);
            setDiscardModalOpen(true);
            return false;
        });
        unblockRef.current = unblock;
        return () => {
            unblock();
            unblockRef.current = null;
        };
    }, [isDirty, isDiscarding, history]);

    // ── Before-unload (browser tab/window close) ──
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (isDirty && !isDiscarding) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isDirty, isDiscarding]);

    // ── Helpers ──────────────────────────────────────────────────────────────
    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setIsDirty(true);
        if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
    };

    const handleSpuSelect = (spu) => {
        if (!spu) { handleSpuClear(); return; }
        setFormData(prev => ({ ...prev, spu, listingBrand: spu.brand, listingModel: spu.model }));
        setIsDirty(true);
        // ERP-5022: the Listing Image now defaults to the brand placeholder for the Listing
        // Device, not the SPU's first image — matching what a listing item does. isPlaceholder
        // marks "no real image chosen" so the backend stores none; isDeviceDefault also opens
        // the picker on the Upload tab with this non-deletable default tile selected. The
        // device can still be unset at this point, which is what the Phone fallback covers.
        setConfirmedImage({
            id: null,
            url: getDeviceDefaultImage(formData.listingDevice),
            isPlaceholder: true,
            isDeviceDefault: true,
        });
        setErrors(prev => ({ ...prev, spu: '', listingBrand: '', listingModel: '', listingImage: '' }));
    };

    const handleSpuClear = () => {
        setFormData(prev => ({ ...prev, spu: null, listingBrand: '', listingModel: '' }));
        setConfirmedImage(null);
        setIsDirty(true);
    };

    const handleImageFieldClick = () => {
        if (!formData.spu) return;
        setImageModalOpen(true);
    };

    const handleImageConfirm = (selectedImg, uploads, removed) => {
        setConfirmedImage(selectedImg);
        setImageModalOpen(false);
        if (selectedImg) setErrors(prev => ({ ...prev, listingImage: '' }));
        setIsDirty(true);

        // `uploads` is already scoped by the picker to this level's own saved history + its own
        // fresh uploads + whichever pool entry got actively selected — so every file uploaded
        // here persists, not just the one currently picked, without also attaching every item's
        // pooled uploads to the listing (ERP-5022).
        setListingUploads((uploads || []).filter(u => u.isSaved || u.file));

        if (!removed?.length) return;
        // Only the level that owns a row can delete it through its own payload. Item-owned
        // entries are stashed and forwarded to Step 2 on Next, which does own them.
        const mine = removed.filter(r => r.source === SOURCE_LISTING).map(r => r.id);
        const theirs = removed.filter(r => r.source !== SOURCE_LISTING);
        if (mine.length) {
            setRemovedListingImageIds(prev => [...prev, ...mine.filter(id => !prev.includes(id))]);
        }
        if (theirs.length) {
            setRemovedPoolImages(prev => [
                ...prev,
                ...theirs.filter(t => !prev.some(p => p.id === t.id && p.source === t.source)),
            ]);
        }
        // The tile is gone from every Upload tab regardless of who owns the underlying row,
        // and so is any other pool entry holding the same picture under a different id.
        setSessionUploads(prev => prev.filter(e => !isRemovedImage(
            { id: e.id, url: e.preview, name: e.name }, removed,
        )));
    };

    // ── Validation ────────────────────────────────────────────────────────────
    const validate = () => {
        const e = {};
        if (!formData.spu || !formData.spu?.id) e.spu = 'SPU is required';
        if (!formData.listingDevice) e.listingDevice = 'Listing Device is required';
        if (!formData.listingBrand?.trim()) e.listingBrand = 'Listing Brand is required';
        if (!formData.listingModel?.trim()) e.listingModel = 'Listing Model is required';
        if (!formData.listingPriority?.trim()) {
            e.listingPriority = 'Listing Priority is required';
        } else if (!/^-?\d+$/.test(formData.listingPriority.trim())) {
            e.listingPriority = 'Specify an integer between -30,000 and 30,000 (inclusive)';
        } else {
            const val = parseInt(formData.listingPriority, 10);
            if (val < -30000 || val > 30000)
                e.listingPriority = 'Specify an integer between -30,000 and 30,000 (inclusive)';
        }
        if (!confirmedImage && !isEditMode) e.listingImage = 'Listing Image is required';
        return e;
    };

    // ── Actions ───────────────────────────────────────────────────────────────
    const handleNext = async () => {
        const newErrors = validate();
        if (formData.id.trim()) {
            setIdCheckLoading(true);
            const isDuplicate = await checkIdExists(formData.id.trim());
            setIdCheckLoading(false);
            if (isDuplicate) newErrors.id = 'ID already exists';
        }
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            showNotification('error', 'Please fix the errors below');
            return;
        }
        // Unblock navigation so the forward push isn't intercepted by the dirty guard
        if (unblockRef.current) {
            unblockRef.current();
            unblockRef.current = null;
        }
        // ERP-3866: if SPU changed since Step 2 data was captured, reset items so Step 2/3 reflect the new SPU's SKUs
        const spuWhenStep2Saved = location.state?.listing?.spu?.id;
        const spuChanged = restoredListingItems && spuWhenStep2Saved && formData.spu?.id !== spuWhenStep2Saved;

        const deviceWhenStep3Saved = location.state?.listing?.listingDevice;
        const deviceChanged = restoredPricingMap && deviceWhenStep3Saved && formData.listingDevice !== deviceWhenStep3Saved;
        history.push(isEditMode ? '/trade-in-listing/edit-listing/items' : '/trade-in-listing/add-listing/items', {
            listing: {
                ...formData,
                listingImage: confirmedImage,
                // The listing's own upload history and removals travel on the listing object so
                // Step 3 can build the save payload from it without another fetch (ERP-5022).
                listingUploads,
                removedListingImageIds,
            },
            listingItems: spuChanged ? null : restoredListingItems,
            isEdit: isEditMode,
            isDirty,
            pricingMap: (spuChanged || deviceChanged) ? null : restoredPricingMap,
            imagePool: sessionUploads,
            removedPoolImages,
        });
    };

    const handleDiscard = () => {
        if (isDirty) setDiscardModalOpen(true);
        else history.push('/trade-in-listing');
    };

    const handleDiscardConfirm = (confirm) => {
        if (confirm) {
            setIsDiscarding(true);
            setIsDirty(false);
            setDiscardModalOpen(false);
            setTimeout(() => {
                if (nextLocation) {
                    const { pathname, state } = nextLocation.location || nextLocation;
                    const unblock = history.block(() => null);
                    history.push(pathname, state);
                    setTimeout(() => unblock(), 0);
                    setNextLocation(null);
                } else {
                    history.push('/trade-in-listing', { successMessage: 'Listing Discarded Successfully' });
                }
            }, 100);
        } else {
            setDiscardModalOpen(false);
            setNextLocation(null);
        }
    };

    // ── SPU images for the picker ──
    // Passed through as-is, matching what Step 2 hands the same component. An empty list is a
    // real answer: the shared picker renders its "No SPU Image Exists" state for it, and the
    // Upload tab's device default is what the listing falls back to.
    //
    // This used to substitute a synthetic [{id: null, url: noImage}] tile when the SPU had no
    // images, because the old SPU-only modal had no empty state and would otherwise have
    // rendered a blank body. Against the shared picker that fallback made the empty state
    // unreachable (length was never 0, and the counter read "Total: 1" for an SPU with none),
    // and it left a selectable tile that was neither a real image nor the device placeholder —
    // confirming it sent listing_image_cleared: false with no resolvable id.
    const currentSpuImages = formData.spu ? (formData.spu.images || []) : [];

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <div className="company-body main-company-body-inner new-look">

            {/* ── Toast notification ── */}
            {notification.show && (
                <AlertNotification
                    type={notification.type}
                    action={notification.action}
                    recordName={notification.recordName}
                    message={notification.message}
                    onClose={() => setNotification(prev => ({ ...prev, show: false }))}
                />
            )}

            {/* ── Breadcrumb ── */}
            <div className="breadcrumb">
                {breadcrumbs.map((crumb, i) => (
                    <span
                        key={i}
                        className={`breadcrumb-item${crumb === 'Trade-In Listing' ? ' clickable' : ''}`}
                        style={{ cursor: crumb === 'Trade-In Listing' ? 'pointer' : 'default' }}
                        onClick={() => crumb === 'Trade-In Listing' && history.push('/trade-in-listing')}
                    >
                        {crumb}{i < breadcrumbs.length - 1 ? '  ' : ''}
                    </span>
                ))}
            </div>

            {/* ── Discard modal ── */}
            <DiscardConfirmationModal
                isOpen={discardModalOpen}
                onCancel={() => { setDiscardModalOpen(false); setNextLocation(null); }}
                onConfirm={handleDiscardConfirm}
                entityName="Trade-In Listing"
            />

            {/* ── Image picker — shared with Step 2's Listing Items (ERP-5022) ── */}
            {imageModalOpen && (
                <TradeInImagePickerModal
                    imageLabel="Listing Image"
                    ownSource={SOURCE_LISTING}
                    images={currentSpuImages}
                    initialSelected={confirmedImage}
                    initialUploadedImages={listingUploads}
                    sessionUploads={sessionUploads}
                    onAddSessionUpload={addSessionUpload}
                    onRemoveSessionUpload={removeSessionUpload}
                    uploadCounterRef={uploadCounterRef}
                    deviceDefaultImage={getDeviceDefaultImage(formData.listingDevice)}
                    // Editing a listing must not flag the files it already owns.
                    excludeListingId={formData.tradein_listing_id}
                    onCancel={() => setImageModalOpen(false)}
                    onConfirm={handleImageConfirm}
                />
            )}

            {/* ── Main card ── */}
            <div className="employee-tabs-container">
                <div className="info-section">
                    <div className="info-card" style={{ overflow: 'visible', padding: 0, background: 'none' }}>

                        {/* Step indicator */}
                        <TradeInStepper currentStep={1} />

                        {/* Card header: title + action buttons */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '20px',
                            borderBottom: '1px solid #E9ECEF',
                            background: 'white',
                            borderRadius: '8px'
                        }}>
                            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, fontFamily: 'Montserrat', color: '#212529' }}>
                                Listing
                            </h2>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button type="button"
                                    className="dark-button"
                                    onClick={handleDiscard}
                                    style={{
                                        borderRadius: '8px',
                                        border: '1px solid #212529',
                                        background: '#FFF',

                                        /* Shadow/Small */
                                        boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.10)',
                                        display: 'flex',
                                        height: '34px',
                                        minWidth: '100px',
                                        padding: '10px',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        gap: '10px',
                                        fontSize: '12px',
                                        fontWeight: 600,
                                    }}> Discard</button>
                                <button type="button" onClick={handleNext}
                                    style={{
                                        borderRadius: '8px',
                                        background: '#212529',

                                        /* Shadow/Small */
                                        boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.10)',
                                        height: '34px',
                                        minWidth: '100px',
                                        padding: '10px',
                                        justifyContent: 'center',
                                        color: 'white',
                                        fontWeight: 600,
                                        fontSize: '12px',
                                        alignItems: 'center',
                                        display: 'flex'
                                    }}>
                                    Next
                                </button>
                            </div>
                        </div>

                        {/* ── Form body ── */}
                        <div style={{ padding: '0px', background: 'white', margin: '12px 0px', borderRadius: '12px' }}>

                            {/* ── Basic Info ── */}
                            <div style={{}}>
                                <div style={{
                                    fontSize: 14, fontWeight: 600, color: '#212529', fontFamily: 'Montserrat',
                                    borderRadius: '12px 12px 0 0',
                                    background: '#F8F9FA', display: 'flex',
                                    height: '50px',
                                    padding: '15px 20px',
                                    alignItems: 'center',
                                    gap: '10px',
                                }}>
                                    Basic Info
                                </div>

                                {/* Row 1: Status | ID */}
                                <div style={{ background: 'white', padding: '20px', borderRadius: '0 0 12px 12px' }}>

                                    <div style={{ ...gridRow }}>

                                        {/* Status */}
                                        <div>
                                            <label className="form-label d-flex align-items-center gap-1" style={labelStyle}>
                                                Status <span style={{ color: '#DC3545' }}>*</span>
                                            </label>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 4 }}>
                                                {['Active', 'Inactive'].map(opt => (
                                                    <CustomRadio
                                                        key={opt}
                                                        name="ti-status"
                                                        value={opt}
                                                        label={opt}
                                                        checked={formData.status === opt}
                                                        onChange={() => handleChange('status', opt)}
                                                    />
                                                ))}
                                                <div></div>
                                            </div>
                                        </div>

                                        {/* ID — same pattern as AddSpu */}
                                        <div className="info-inner">
                                            <label htmlFor="ti-id" className="form-label d-flex align-items-center gap-1" style={labelStyle}>
                                                ID
                                            </label>
                                            <div style={{ position: 'relative' }}>
                                                <input
                                                    type="text"
                                                    id="ti-id"
                                                    className="form-control common-input-all"
                                                    value={formData.id}
                                                    onChange={(e) => handleChange('id', e.target.value)}
                                                    onBlur={async (e) => {
                                                        const val = e.target.value.trim();
                                                        if (!val) return;
                                                        const isDuplicate = await checkIdExists(val);
                                                        if (isDuplicate) setErrors(prev => ({ ...prev, id: 'ID already exists' }));
                                                    }}
                                                    style={{ ...(errors.id ? { borderColor: '#DC3545' } : { border: '1px solid #6C757D' }), color: '#212529', paddingRight: formData.id ? 28 : undefined }}
                                                />
                                                {formData.id && (
                                                    <button type="button" onClick={() => handleChange('id', '')} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                            <path d="M9 3L3 9M3 3L9 9" stroke="black" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    </button>
                                                )}
                                            </div>
                                            {errors.id && (
                                                <div className="invalid-feedback d-block">{errors.id}</div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Row 2: SPU | Listing Device */}
                                    <div style={gridRow}>

                                        {/* SPU */}
                                        <div className="info-inner">
                                            {isEditMode && formData.spu?.name ? (
                                                <>
                                                    <label className="form-label d-flex align-items-center gap-1" style={labelStyle}>
                                                        SPU  <span style={{ color: '#DC3545' }}>*</span>
                                                    </label>
                                                    <div style={{
                                                        display: 'flex', alignItems: 'center', gap: 8,
                                                        border: '1px solid #6C757D', borderRadius: 8,
                                                        padding: '10px 12px', height: 44,
                                                        background: '#F8F9FA',
                                                        fontSize: 14, fontFamily: 'Montserrat', color: '#6C757D',
                                                        cursor: 'not-allowed',
                                                    }}>
                                                        {formData.spu?.name || '—'}
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <label className="form-label d-flex align-items-center gap-1" style={labelStyle}>
                                                        SPU <span style={{ color: '#DC3545' }}>*</span>
                                                    </label>
                                                    <SpuSearchDropdown
                                                        selected={formData.spu}
                                                        onSelect={handleSpuSelect}
                                                        onClear={handleSpuClear}
                                                        error={!!errors.spu}
                                                    />
                                                    {errors.spu && <div className="invalid-feedback d-block">{errors.spu}</div>}
                                                </>
                                            )}
                                        </div>

                                        {/* Listing Device */}
                                        <div className="info-inner">
                                            {isEditMode ? (
                                                <>
                                                    <label className="form-label d-flex align-items-center gap-1" style={labelStyle}>
                                                        Listing Device <span style={{ color: '#DC3545' }}>*</span>
                                                    </label>
                                                    <div style={{
                                                        display: 'flex', alignItems: 'center', gap: 8,
                                                        border: '1px solid #6C757D', borderRadius: 8,
                                                        padding: '10px 12px', height: 44,
                                                        background: '#F8F9FA',
                                                        fontSize: 14, fontFamily: 'Montserrat', color: '#6C757D',
                                                        cursor: 'not-allowed',
                                                    }}>
                                                        {formData.listingDevice || '—'}
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <label className="form-label d-flex align-items-center gap-1" style={labelStyle}>
                                                        Listing Device <span style={{ color: '#DC3545' }}>*</span>
                                                    </label>
                                                    <div ref={deviceRef} style={{ position: 'relative' }}>
                                                        <div
                                                            style={{
                                                                ...dropdownTriggerStyle,
                                                                border: errors.listingDevice ? '1px solid #DC3545' : '1px solid #6C757D',
                                                            }}
                                                            onClick={() => setDeviceOpen(p => !p)}
                                                        >
                                                            <span style={{ fontSize: 14, color: formData.listingDevice ? '#212529' : '#6C757D', fontFamily: 'Montserrat', flex: 1 }}>
                                                                {formData.listingDevice || 'Select'}
                                                            </span>
                                                            {formData.listingDevice ? (
                                                                <button type="button" onClick={(e) => { e.stopPropagation(); handleChange('listingDevice', ''); }} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                                                                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                                        <path d="M9 3L3 9M3 3L9 9" stroke="black" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                                                    </svg>
                                                                </button>
                                                            ) : (
                                                                <ChevronDown size={16} color="#6C757D"
                                                                    style={{ transition: 'transform 0.2s', transform: deviceOpen ? 'rotate(180deg)' : 'none', flexShrink: 0 }} />
                                                            )}
                                                        </div>
                                                        {deviceOpen && (
                                                            <div style={dropdownMenuStyle}>
                                                                {deviceOptions.map(opt => (
                                                                    <div
                                                                        key={opt}
                                                                        onClick={() => { handleChange('listingDevice', opt); setDeviceOpen(false); }}
                                                                        style={{ ...dropdownItemStyle, background: formData.listingDevice === opt ? '#FFFEF0' : '' }}
                                                                        onMouseEnter={e => e.currentTarget.style.background = '#FFFEF0'}
                                                                        onMouseLeave={e => e.currentTarget.style.background = formData.listingDevice === opt ? '#FFFEF0' : ''}
                                                                    >
                                                                        {opt}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                    {errors.listingDevice && <div className="invalid-feedback d-block">{errors.listingDevice}</div>}
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    {/* Row 3: Listing Brand | Listing Model */}
                                    <div style={gridRow}>

                                        {/* Listing Brand */}
                                        <div className="info-inner">
                                            <label className="form-label d-flex align-items-center gap-1" style={labelStyle}>
                                                Listing Brand <span style={{ color: '#DC3545' }}>*</span>
                                            </label>
                                            <div style={{ position: 'relative' }}>
                                                <input
                                                    type="text"
                                                    className="form-control common-input-all"
                                                    value={formData.listingBrand}
                                                    onChange={(e) => handleChange('listingBrand', e.target.value)}
                                                    disabled={!formData.spu}
                                                    style={
                                                        !formData.spu
                                                            ? { border: '1px solid #6C757D', color: '#ADB5BD', background: '#F8F9FA', cursor: 'not-allowed' }
                                                            : errors.listingBrand
                                                                ? { borderColor: '#DC3545', color: '#212529', paddingRight: formData.listingBrand ? 28 : undefined }
                                                                : { border: '1px solid #6C757D', color: '#212529', paddingRight: formData.listingBrand ? 28 : undefined }
                                                    }
                                                />
                                                {formData.spu && formData.listingBrand && (
                                                    <button type="button" onClick={() => handleChange('listingBrand', '')} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                            <path d="M9 3L3 9M3 3L9 9" stroke="black" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    </button>
                                                )}
                                            </div>
                                            {errors.listingBrand && <div className="invalid-feedback d-block">{errors.listingBrand}</div>}
                                        </div>

                                        {/* Listing Model */}
                                        <div className="info-inner">
                                            <label className="form-label d-flex align-items-center gap-1" style={labelStyle}>
                                                Listing Model <span style={{ color: '#DC3545' }}>*</span>
                                            </label>
                                            <div style={{ position: 'relative' }}>
                                                <input
                                                    type="text"
                                                    className="form-control common-input-all"
                                                    value={formData.listingModel}
                                                    onChange={(e) => handleChange('listingModel', e.target.value)}
                                                    disabled={!formData.spu}
                                                    style={
                                                        !formData.spu
                                                            ? { border: '1px solid #6C757D', color: '#ADB5BD', background: '#F8F9FA', cursor: 'not-allowed' }
                                                            : errors.listingModel
                                                                ? { borderColor: '#DC3545', color: '#212529', paddingRight: formData.listingModel ? 28 : undefined }
                                                                : { border: '1px solid #6C757D', color: '#212529', paddingRight: formData.listingModel ? 28 : undefined }
                                                    }
                                                />
                                                {formData.spu && formData.listingModel && (
                                                    <button type="button" onClick={() => handleChange('listingModel', '')} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                            <path d="M9 3L3 9M3 3L9 9" stroke="black" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    </button>
                                                )}
                                            </div>
                                            {errors.listingModel && <div className="invalid-feedback d-block">{errors.listingModel}</div>}
                                        </div>
                                    </div>

                                    {/* Row 4: Listing Priority | (empty) */}
                                    <div style={gridRow}>
                                        <div className="info-inner">
                                            <label className="form-label d-flex align-items-center gap-1" style={labelStyle}>
                                                Listing Priority <span style={{ color: '#DC3545' }}>*</span>
                                                <Tooltip
                                                    placement="top"
                                                    title="Listing Priority"
                                                    body="An integer between -30,000 and 30,000 (inclusive) that determines this Listing's display order among all Listings. Higher values appear first."
                                                >
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
                                                        <Info size={18} color="#000" />
                                                    </span>
                                                </Tooltip>
                                            </label>
                                            <div style={{ position: 'relative' }}>
                                                <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    className="form-control common-input-all"
                                                    value={formData.listingPriority}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        if (val === '' || val === '-' || /^-?\d+$/.test(val)) {
                                                            handleChange('listingPriority', val);
                                                        }
                                                    }}
                                                    onBlur={(e) => {
                                                        const val = e.target.value;
                                                        let msg = '';
                                                        if (!val?.trim()) {
                                                            msg = 'Listing Priority is required';
                                                        } else if (!/^-?\d+$/.test(val.trim())) {
                                                            msg = 'Specify an integer between -30,000 and 30,000 (inclusive)';
                                                        } else {
                                                            const num = parseInt(val, 10);
                                                            if (num < -30000 || num > 30000) msg = 'Specify an integer between -30,000 and 30,000 (inclusive)';
                                                        }
                                                        setErrors(prev => ({ ...prev, listingPriority: msg }));
                                                    }}
                                                    style={{ ...(errors.listingPriority ? { borderColor: '#DC3545' } : { border: '1px solid #6C757D' }), color: '#212529', paddingRight: formData.listingPriority ? 28 : undefined }}
                                                />
                                                {formData.listingPriority && (
                                                    <button type="button" onClick={() => handleChange('listingPriority', '')} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                            <path d="M9 3L3 9M3 3L9 9" stroke="black" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    </button>
                                                )}
                                            </div>
                                            {errors.listingPriority && <div className="invalid-feedback d-block">{errors.listingPriority}</div>}
                                        </div>
                                        <div /> {/* empty right column */}
                                    </div>

                                    {/* Row 5: Listing Image | (empty) */}
                                    <div style={{ ...gridRow, marginBottom: '0px' }}>
                                        <div className="info-inner">
                                            <label className="form-label d-flex align-items-center gap-1" style={labelStyle}>
                                                Listing Image <span style={{ color: '#DC3545' }}>*</span>
                                            </label>

                                            {/* Clickable image area */}
                                            <div
                                                onClick={handleImageFieldClick}
                                                style={{
                                                    border: `1px dashed ${errors.listingImage ? '#DC3545' : '#CED4DA'}`,
                                                    borderRadius: 8,
                                                    height: 160,
                                                    cursor: formData.spu ? 'pointer' : 'default',
                                                    background: '#FAFAFA',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    position: 'relative',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                {confirmedImage ? (
                                                    /* Selected image preview */
                                                    <>
                                                        {/* Never scale past the source's own pixels. The device
                                                            placeholders are 98x98 and some SPU images are small
                                                            too — stretching one to fill this 160px box is what
                                                            makes it look blurry. max-* lets a large image shrink
                                                            to fit while a small one renders 1:1 and stays sharp. */}
                                                        <img
                                                            src={confirmedImage.url || noImage}
                                                            alt="Listing"
                                                            onError={e => { e.target.onerror = null; e.target.src = noImage; }}
                                                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                                        />
                                                        {/* Edit / Remove overlay */}
                                                        <div style={{ position: 'absolute', top: 6, right: 6, display: 'flex', gap: 4 }}>
                                                            <button
                                                                type="button"
                                                                title="Change image"
                                                                onClick={(e) => { e.stopPropagation(); setImageModalOpen(true); }}
                                                                style={imageActionBtnStyle}
                                                            >
                                                                <Edit2 size={12} color="#fff" />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                title="Remove image"
                                                                onClick={(e) => { e.stopPropagation(); setConfirmedImage(null); setIsDirty(true); }}
                                                                style={imageActionBtnStyle}
                                                            >
                                                                <X size={12} color="#fff" />
                                                            </button>
                                                        </div>
                                                    </>
                                                ) : !formData.spu ? (
                                                    /* Locked state — no SPU selected */
                                                    <>
                                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                                                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                                                                <rect x="3" y="11" width="18" height="11" rx="2" stroke="#ADB5BD" strokeWidth="2" />
                                                                <path d="M7 11V7a5 5 0 0110 0v4" stroke="#ADB5BD" strokeWidth="2" />
                                                            </svg>
                                                            <span style={{ fontSize: 12, color: '#ADB5BD', fontFamily: 'Montserrat', textAlign: 'center' }}>
                                                                Select Image Disabled
                                                            </span>
                                                        </div>
                                                        {/* Ban icon */}
                                                        <svg style={{ position: 'absolute', bottom: 8, right: 8 }} width="20" height="20" viewBox="0 0 24 24" fill="none">
                                                            <circle cx="12" cy="12" r="9" stroke="#ADB5BD" strokeWidth="2" />
                                                            <line x1="4.5" y1="4.5" x2="19.5" y2="19.5" stroke="#ADB5BD" strokeWidth="2" strokeLinecap="round" />
                                                        </svg>
                                                    </>
                                                ) : (
                                                    /* SPU selected, no image chosen */
                                                     <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                                                        {/* Hand pointer icon */}
                                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M9 11V6a2 2 0 1 1 4 0v5" stroke="#212529" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                            <path d="M13 10a2 2 0 1 1 4 0v2" stroke="#212529" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                            <path d="M17 12a2 2 0 1 1 4 0v2c0 3.314-2.686 6-6 6H12c-1.6 0-3-.7-4-2l-3-4a2 2 0 0 1 3-2.5l1 1.5V6" stroke="#212529" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                            <path d="M9 6V4a2 2 0 1 0-4 0v7" stroke="#212529" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                        </svg>
                                                        <span style={{ fontSize: 12, color: '#6C757D', fontFamily: 'Montserrat', textAlign: 'center' }}>
                                                            Select an SPU Image as Listing Image{' '}
                                                            <span
                                                                onClick={(e) => { e.stopPropagation(); setImageModalOpen(true); }}
                                                                style={{ color: '#0D6EFD', fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}
                                                            >
                                                                Select
                                                            </span>
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                            {errors.listingImage && <div className="invalid-feedback d-block">{errors.listingImage}</div>}
                                        </div>
                                        <div /> {/* empty right column */}
                                    </div>
                                </div>

                            </div>
                        </div>

                        {/* ── Notes ── */}
                        <div style={{ background: 'white', borderRadius: '12px' }}>
                            <div style={{
                                fontSize: 14, fontWeight: 600, color: '#212529', fontFamily: 'Montserrat', borderRadius: '12px 12px 0 0',
                                height: '50px',
                                padding: '15px 20px',
                                alignItems: 'center',
                                gap: '10px',
                                background: '#F8F9FA', display: 'flex',
                                alignItems: 'center',
                            }}>
                                Notes
                            </div>
                            <div style={{ padding: '20px' }}>

                                <textarea
                                    value={formData.notes}
                                    onChange={(e) => handleChange('notes', e.target.value)}
                                    rows={4}
                                    style={{
                                        width: '100%',
                                        border: '1px solid #CED4DA',
                                        borderRadius: 8,
                                        padding: '10px 12px',
                                        fontSize: 14,
                                        fontFamily: 'Montserrat',
                                        resize: 'vertical',
                                        outline: 'none',
                                        color: '#212529',
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Remove number input spinners */}
            <style>{`
                input[type=number]::-webkit-outer-spin-button,
                input[type=number]::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
                input[type=number] { -moz-appearance: textfield; }
            `}</style>
        </div>
    );
}

// ─── Shared styles ────────────────────────────────────────────────────────────
const gridRow = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 20,
    marginBottom: 20,
};

const labelStyle = {
    fontSize: 12,
    fontWeight: 700,
    fontFamily: 'Montserrat',
    color: '#212529',
    marginBottom: 10,

};

const dropdownTriggerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRadius: 8,
    padding: '0 12px',
    cursor: 'pointer',
    background: '#fff',
    height: 40,
    gap: 8,
};

const dropdownMenuStyle = {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0, right: 0,
    background: '#fff',
    border: '1px solid #CED4DA',
    borderRadius: 8,
    boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
    zIndex: 1000,
};

const dropdownItemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 12px',
    cursor: 'pointer',
    fontSize: 14,
    fontFamily: 'Montserrat',
    color: '#212529',
};

const iconBtnStyle = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 2,
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
};

const imageActionBtnStyle = {
    background: 'rgba(0,0,0,0.55)',
    border: 'none',
    borderRadius: '50%',
    width: 24, height: 24,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer',
    padding: 0,
};
